import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { MyExamAttempt } from '@proref/types';
import type {
  UpdateExamInput,
  CreateExamQuestionInput,
  UpdateExamQuestionInput,
  AddExamClipQuestionsInput,
  AddExamRuleQuestionsInput,
} from '@proref/schemas';
import type { SaveExamAnswerInput, StartPracticeTestInput } from '@proref/schemas';
import {
  listExams,
  getExam,
  createExam,
  updateExam,
  deleteExam,
  publishExam,
  unpublishExam,
  addExamQuestion,
  addExamClipQuestions,
  addExamRuleQuestions,
  updateExamQuestion,
  deleteExamQuestion,
  listMyAttempts,
  startExamAttempt,
  startPracticeTest,
  getExamAttempt,
  saveExamAnswer,
  submitExamAttempt,
  getExamResults,
  abandonExamAttempt,
  listExamResults,
  getExamResultDetail,
  setExamResultsReleased,
  deleteExamResult,
  type ListExamsParams,
} from '../services/exams.service';

// The exam list for the admin console. Key includes the filter object per convention. Short
// stale window — an author flips publish state / adds exams and expects the list to reflect it.
export function useExamsQuery(params: ListExamsParams = {}) {
  return useQuery({
    queryKey: ['exams', params],
    queryFn: () => listExams(params),
    staleTime: 15_000,
    // Show the previous tab's rows while the new key loads, instead of dropping to a skeleton.
    // This does NOT make data staler: staleTime and invalidation are untouched, so publishing or
    // adding an exam still refetches and replaces this immediately. It only fills the gap where a
    // key change has no cached entry yet — `isPlaceholderData` marks it provisional on screen.
    placeholderData: keepPreviousData,
  });
}

// One exam with its questions — the editor's source of truth. Disabled until an id is present.
export function useExamQuery(id: string | undefined) {
  return useQuery({
    queryKey: ['exam', id],
    queryFn: () => getExam(id as string),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}

export function useCreateExam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createExam,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['exams'] }),
  });
}

export function useUpdateExam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateExamInput }) => updateExam(id, input),
    onSuccess: (exam) => {
      void qc.invalidateQueries({ queryKey: ['exams'] });
      void qc.invalidateQueries({ queryKey: ['exam', exam.id] });
    },
  });
}

export function useDeleteExam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteExam,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['exams'] }),
  });
}

// Publish + unpublish share the same invalidation — both flip the exam's status in the list
// and the editor.
function useTogglePublish(fn: (id: string) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['exams'] });
      void qc.invalidateQueries({ queryKey: ['exam', id] });
    },
  });
}
export const usePublishExam = () => useTogglePublish(publishExam);
export const useUnpublishExam = () => useTogglePublish(unpublishExam);

// Question mutations all invalidate the parent exam so the editor's question list refreshes.
export function useAddExamQuestion(examId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateExamQuestionInput) => addExamQuestion(examId, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['exam', examId] }),
  });
}

// Add several clip questions in one call — the multi-select "build from selected videos" flow.
// Invalidates the parent exam so the editor's question list refreshes with all the new rows.
export function useAddExamClipQuestions(examId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddExamClipQuestionsInput) => addExamClipQuestions(examId, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['exam', examId] }),
  });
}

// Add several rule-test questions in one atomic call — the "stage from the library, save once"
// flow. Invalidates the parent exam so the editor's question list refreshes with all new rows.
export function useAddExamRuleQuestions(examId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddExamRuleQuestionsInput) => addExamRuleQuestions(examId, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['exam', examId] }),
  });
}

export function useUpdateExamQuestion(examId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateExamQuestionInput }) => updateExamQuestion(id, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['exam', examId] }),
  });
}

export function useDeleteExamQuestion(examId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteExamQuestion(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['exam', examId] }),
  });
}

// ── Taking (referee) ─────────────────────────────────────────────────────────

// The caller's attempts — resume banner + recent results. Short stale window so a just-finished
// attempt shows up on the landing.
export function useMyExamAttemptsQuery() {
  return useQuery({
    queryKey: ['exam-attempts', 'mine'],
    queryFn: listMyAttempts,
    staleTime: 15_000,
  });
}

// One attempt's live state (the runner). No stale window — the runner drives its own countdown
// and autosaves; refetching mid-attempt would clobber unsaved local selection.
export function useExamAttemptQuery(attemptId: string | undefined) {
  return useQuery({
    queryKey: ['exam-attempt', attemptId],
    queryFn: () => getExamAttempt(attemptId as string),
    enabled: Boolean(attemptId),
    staleTime: Infinity,
  });
}

// Start a generated practice test. Invalidates the attempt list so the new sitting shows up in
// history (and on the resume banner if they walk away mid-test).
export function useStartPracticeTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: StartPracticeTestInput) => startPracticeTest(input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['exam-attempts', 'mine'] }),
  });
}

export function useStartExamAttempt() {
  const qc = useQueryClient();
  return useMutation({
    // password is supplied only for Official (proctored) exams.
    mutationFn: ({ examId, password }: { examId: string; password?: string }) => startExamAttempt(examId, password),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['exam-attempts', 'mine'] }),
  });
}

// Abandon an in-progress attempt → drop it off the resume banner. We remove the row from the
// cached list synchronously (not just invalidate) so the banner clears instantly regardless of
// refetch timing — a stale-window refetch alone left the just-abandoned attempt on screen. Then
// invalidate to reconcile with the server (and surface the next in-progress attempt, if any).
export function useAbandonExamAttempt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attemptId: string) => abandonExamAttempt(attemptId),
    onSuccess: (_data, attemptId) => {
      qc.setQueryData<MyExamAttempt[]>(['exam-attempts', 'mine'], (prev) =>
        prev ? prev.filter((a) => a.id !== attemptId) : prev,
      );
      void qc.invalidateQueries({ queryKey: ['exam-attempts', 'mine'] });
    },
  });
}

// Autosave — no invalidation (the runner holds local state; we don't want a refetch per keystroke).
export function useSaveExamAnswer(attemptId: string) {
  return useMutation({
    mutationFn: (input: SaveExamAnswerInput) => saveExamAnswer(attemptId, input),
  });
}

export function useSubmitExamAttempt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attemptId: string) => submitExamAttempt(attemptId),
    onSuccess: (_r, attemptId) => {
      void qc.invalidateQueries({ queryKey: ['exam-attempts', 'mine'] });
      void qc.invalidateQueries({ queryKey: ['exam-results', attemptId] });
    },
  });
}

// Post-submit results — immutable once graded, so cache it hard.
export function useExamResultsQuery(attemptId: string | undefined) {
  return useQuery({
    queryKey: ['exam-results', attemptId],
    queryFn: () => getExamResults(attemptId as string),
    enabled: Boolean(attemptId),
    staleTime: Infinity,
  });
}

// ── Official results (admin) ─────────────────────────────────────────────────

// The cohort table for one exam. Short stale window rather than Infinity: an admin watches this
// page while a sitting is in progress and expects new submissions to appear.
export function useExamResultsQuery_Admin(examId: string | undefined) {
  return useQuery({
    queryKey: ['exam-results-admin', examId],
    queryFn: () => listExamResults(examId as string),
    enabled: Boolean(examId),
    staleTime: 30_000,
  });
}

// One sitting from the cohort table. A submitted attempt's breakdown never changes, so it caches
// as hard as the candidate's own results view does.
export function useExamResultDetailQuery(examId: string | undefined, attemptId: string | undefined) {
  return useQuery({
    queryKey: ['exam-result-detail', examId, attemptId],
    queryFn: () => getExamResultDetail(examId as string, attemptId as string),
    enabled: Boolean(examId && attemptId),
    staleTime: Infinity,
  });
}

// Release / withhold the answer breakdown. Invalidates the exam itself (the toggle reads
// `results_released_at` off it) and the list that renders its badge.
export function useSetExamResultsReleased(examId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (released: boolean) => setExamResultsReleased(examId, released),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['exam', examId] });
      void qc.invalidateQueries({ queryKey: ['exams'] });
    },
  });
}

export function useDeleteExamResult(examId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attemptId: string) => deleteExamResult(examId, attemptId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['exam-results-admin', examId] }),
  });
}
